import { Fragment } from 'react';
import { NAV_ITEMS } from './navItems';
import { NavItem } from './NavItem';
import { useCurrentRole } from '../../hooks/usePermissions';
import { canAccessFeature } from '../../services/auth/permissions';
import { useTicketCounts } from '../../hooks/queries/useTickets';
import { useTotalUnreadCount } from '../../hooks/queries/useMessaging';
import { useGmailQueuedCount } from '../../hooks/queries/useGmailInbox';
import { useTotalParapheurPending } from '../../hooks/queries/useSignatureCircuits';
import { useSidecarStore } from '../../store/useSidecarStore';
import type { NavItemDef } from './navItems';

interface SidebarNavProps {
  collapsed: boolean;
}

export function SidebarNav({ collapsed }: SidebarNavProps) {
  const role = useCurrentRole();
  const { data: ticketCounts } = useTicketCounts();
  const messagingUnread = useTotalUnreadCount();
  const { data: gmailQueued } = useGmailQueuedCount();
  const parapheurPending = useTotalParapheurPending();
  const openSidecar = useSidecarStore((s) => s.open);

  function getOnClick(action: NavItemDef['action']): (() => void) | undefined {
    if (action === 'open-messaging-sidecar') return openSidecar;
    return undefined;
  }

  const filteredItems = NAV_ITEMS.filter((item) => {
    if (!item.feature) return true;
    return canAccessFeature(role, item.feature);
  });

  function getBadgeCount(source: 'tickets_unseen' | 'messaging_unread' | 'gmail_queued' | 'parapheur_pending' | undefined): number | undefined {
    if (!source) return undefined;
    if (source === 'tickets_unseen') {
      const count = ticketCounts?.unseen_mine ?? 0;
      return count > 0 ? count : undefined;
    }
    if (source === 'messaging_unread') {
      return messagingUnread > 0 ? messagingUnread : undefined;
    }
    if (source === 'gmail_queued') {
      const count = gmailQueued ?? 0;
      return count > 0 ? count : undefined;
    }
    if (source === 'parapheur_pending') {
      return parapheurPending > 0 ? parapheurPending : undefined;
    }
    return undefined;
  }

  return (
    <nav
      aria-label="Navigation principale"
      className="py-2 space-y-0.5"
    >
      {filteredItems.map((item, index) => (
        <Fragment key={item.to}>
          <NavItem
            icon={item.icon}
            label={item.label}
            to={item.to}
            disabled={item.disabled}
            collapsed={collapsed}
            indent={item.indent}
            badgeCount={getBadgeCount(item.badgeCountSource)}
            onClick={getOnClick(item.action)}
          />
          {item.separatorAfter && index < filteredItems.length - 1 ? (
            <hr className="my-2 border-cime-sidebar-border" />
          ) : null}
        </Fragment>
      ))}
    </nav>
  );
}
